import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";

const WhatsAppWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const phoneNumber = "14759885714"; // Connecticut, USA
  const defaultMessage = "Hola, me interesa conocer más sobre sus servicios digitales.";

  const handleWhatsAppClick = () => {
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(defaultMessage)}`;
    window.open(url, "_blank");
  };

  return (
    <>
      {/* Floating Button */}
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1, type: "spring", stiffness: 200 }}
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-8 right-8 z-50 w-16 h-16 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 ${
          isOpen
            ? "bg-zinc-800 hover:bg-zinc-700"
            : "bg-[#25D366] hover:bg-[#20BA5C] whatsapp-pulse"
        }`}
        data-testid="whatsapp-widget-button"
        aria-label="Abrir chat de WhatsApp"
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <X className="w-6 h-6 text-white" />
            </motion.div>
          ) : (
            <motion.div
              key="whatsapp"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <MessageCircle className="w-7 h-7 text-white" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Chat Popup */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-28 right-8 z-50 w-80 rounded-3xl overflow-hidden shadow-2xl"
            data-testid="whatsapp-widget-popup"
          >
            {/* Header */}
            <div className="bg-[#25D366] p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="text-white font-semibold">NexusDigital</h4>
                  <p className="text-white/80 text-sm">
                    Normalmente respondemos en minutos
                  </p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="bg-[#0A0A0A] p-4">
              {/* Message bubble */}
              <div className="bg-white/5 rounded-2xl rounded-tl-sm p-4 mb-4">
                <p className="text-zinc-300 text-sm">
                  ¡Hola! 👋 ¿En qué podemos ayudarte hoy? Cuéntanos sobre tu proyecto y te asesoraremos sin compromiso.
                </p>
                <span className="text-zinc-500 text-xs mt-2 block">
                  Equipo klyvora7
                </span>
              </div>

              {/* CTA Button */}
              <button
                onClick={handleWhatsAppClick}
                className="w-full bg-[#25D366] hover:bg-[#20BA5C] text-white py-3 rounded-full font-medium flex items-center justify-center gap-2 transition-all duration-300"
                data-testid="whatsapp-widget-send"
              >
                <Send className="w-5 h-5" />
                Iniciar Conversación
              </button>
            </div>

            {/* Footer */}
            <div className="bg-[#0A0A0A] border-t border-white/5 px-4 py-3">
              <p className="text-zinc-500 text-xs text-center">
                Powered by WhatsApp Business
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tooltip (shown when chat is closed) */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ delay: 2 }}
            className="fixed bottom-10 right-28 z-40 bg-white text-zinc-900 px-4 py-2 rounded-xl shadow-lg text-sm font-medium hidden md:block"
          >
            ¿Necesitas ayuda?
            <div className="absolute top-1/2 -right-2 -translate-y-1/2 w-0 h-0 border-t-8 border-b-8 border-l-8 border-transparent border-l-white" />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default WhatsAppWidget;
